import PropTypes from 'prop-types'
import { useCallback, useEffect, useReducer, useState } from 'react'
import { Col, Row, Label, FormGroup, Select } from 'twyne-new.ui'

import { PureSwitch } from 'components/form/Switch';

const propTypes = {
  options: PropTypes.array,
  defaultValue: PropTypes.array,
  onChange: PropTypes.func,
}

export default function PivotByConfigure({ options, defaultValue, onChange }) {
  const [dimensions, setDimensions] = useState([])
  const [metrics, setMetrics] = useState([])
  const [metricAggregator, setMetricAggregator] = useState(false)

  const reducer = useCallback((state, update) => {
    const props = { ...state, ...update }
    onChange(props)
    return props
  }, [onChange])
  const [state, handleChange] = useReducer(reducer, DEFAULT)
  const { cols, rows, metric, aggregator, sort, formatter, showHeatmap, heatmapColor } = state

  useEffect(() => {
    const { dimensions, metrics } = options
    setDimensions(dimensions)
    setMetrics(metrics)
  }, [options])

  useEffect(() => {
    if (state !== defaultValue) {
      handleChange(defaultValue)
    }
  }, [defaultValue])

  useEffect(() => {
    if (metric) {
      setMetricAggregator(metrics.some(({ value, aggregator }) => value === metric && aggregator))
    }
  }, [metric, metrics])

  return <>
    <Row>
      <Col size="12">
        <FormGroup>
          <Label>Columns</Label>
          <SelectMulti options={dimensions} clearable={false} values={cols}
            hide={rows} onChange={value => handleChange({ cols: value })} />
        </FormGroup>
      </Col>
      <Col size="12">
        <FormGroup>
          <Label>Rows</Label>
          <SelectMulti options={dimensions} clearable={false} values={rows}
            hide={cols} onChange={value => handleChange({ rows: value })} />
        </FormGroup>
      </Col>
      <Col size="4">
        <FormGroup>
          <Label>Metric</Label>
          <Select options={metrics} value={metric} clearable={false}
            onChange={option => handleChange({ metric: option.value })} />
        </FormGroup>
      </Col>
      <Col size="4">
        <FormGroup>
          <Label>Aggregator</Label>
          <Select options={AGGREGATORS} value={aggregator} disabled={!metricAggregator}
            onChange={option => handleChange({ aggregator: option.value })} clearable={false} />
        </FormGroup>
      </Col>
      <Col size="4">
        <FormGroup>
          <Label>Sort</Label>
          <Select options={SORTERS} value={sort} clearable={false}
            onChange={option => handleChange({ sort: option.value })} />
        </FormGroup>
      </Col>
      <Col size="6">
        <FormGroup>
          <Label>Formatter</Label>
          <Select options={FORMATTERS} value={formatter} clearable={false}
            onChange={option => handleChange({ formatter: option.value })} />
        </FormGroup>
      </Col>
      <Col size="2">
        <FormGroup>
          <Label>Heatmap</Label>
          <PureSwitch value={showHeatmap} onChange={event => handleChange({ showHeatmap: event.target.checked })} />
        </FormGroup>
      </Col>
      <Col size="4">
        <FormGroup>
          <Label>Colors</Label>
          <Select options={COLORS} value={heatmapColor} clearable={false}
            onChange={option => handleChange({ heatmapColor: option.value })} disabled={!showHeatmap} />
        </FormGroup>
      </Col>
    </Row>
  </>
}

PivotByConfigure.propTypes = propTypes
PivotByConfigure.defaultProps = {}

function SelectMulti({ options, values, hide, onChange }) {
  const filteredOptions = options.filter(option => !hide.includes(option.value))
  const handleChange = useCallback(options => {
    const values = options.map(({ value }) => value)
    if (values.length <= 4) onChange(values)
  }, [onChange])

  return <Select multi value={values} options={filteredOptions} onChange={handleChange} />
}

const DEFAULT = {
  cols: [],
  rows: [],
  metric: [],
  aggregator: '',
  formatter: '',
  showHeatmap: false,
  heatmapColor: 'blue',
}

const AGGREGATORS = [
  { label: "Count", value: "cardinality" },
  { label: "Sum", value: "sum" },
  { label: "Average", value: "avg" },
  { label: "Minimum", value: "min" },
  { label: "Maximum", value: "max" },
]

const SORTERS = [
  { label: "Asc", value: "asc" },
  { label: "Desc", value: "desc" },
]

const FORMATTERS = [
  { label: "Money", value: "money" },
  { label: "Number", value: "number" },
]

const COLORS = [
  { label: "Heatmap", value: "heatmap" },
  { label: "Blue", value: "blue" },
]
